import { useEffect, useState } from 'react';
import {
    SelectionSet,
    SelectionSetInput,
    SelectionSetLabel,
    Typography,
    tokensRaw,
} from '@vp/swan';
import styled from 'styled-components';
import { __ } from '@99designs/i18n';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { briefTestId } from '../../testIds';
import { RadioChoiceFieldProps } from './RadioChoiceField';
import { useUpdateRadioChoiceField } from './useUpdateRadioChoiceField';

const Label = styled.label`
    display: inline-block;
    padding-bottom: ${tokensRaw.SwanSemSpace2};
`;

export function SelectRadioChoiceField({
    id,
    register,
    required,
    setValue,
    choiceValue,
    options,
    immutable,
    errorMessage,
}: RadioChoiceFieldProps) {
    const DEFAULT_VALUE = options[0].value;
    const [selectedValue, setSelectedValue] = useState<string | null>(choiceValue || DEFAULT_VALUE);
    const disabled = !!immutable && choiceValue !== '';
    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateRadioChoiceField(id, briefId, selectedValue);
    const errorMsg = errorMessage || __('This field is required');

    useEffect(() => {
        setValue(id, selectedValue);

        if (selectedValue) {
            updateMutation(selectedValue);
        }
    }, [id, setValue, selectedValue, updateMutation]);

    return (
        <SelectionSet
            data-testid={briefTestId('brief-field-radio-group', id)}
            aria-label={id}
            variant="single-select"
            skin="simple-column"
            selectedValue={selectedValue}
            onSelectedValueChange={(newSelectedValue) => setSelectedValue(newSelectedValue)}
        >
            {options?.map(({ value, label, subtext }) => (
                <SelectionSetInput
                    key={value}
                    value={value}
                    data-testid={briefTestId('brief-field-radio-option', id, value)}
                    {...register(id, {
                        required: required ? errorMsg : false,
                        disabled,
                    })}
                    disabled={disabled}
                    aria-required={required ? true : undefined}
                >
                    <SelectionSetLabel
                        data-testid={briefTestId('brief-field-radio-option-label', id, value)}
                    >
                        <Label htmlFor={value}>
                            <Typography
                                fontSize="standard"
                                fontWeight="bold"
                                data-testid={briefTestId(
                                    'brief-field-radio-option-title',
                                    id,
                                    value
                                )}
                            >
                                {label}
                            </Typography>
                        </Label>
                        <Typography
                            fontSize="standard"
                            data-testid={briefTestId('brief-field-radio-option-subtext', id, value)}
                        >
                            {subtext}
                        </Typography>
                    </SelectionSetLabel>
                </SelectionSetInput>
            ))}
        </SelectionSet>
    );
}
